import type { DeepDivePhase } from '../../config/deepDive';
import {
  type DeepDiveAnalysis,
  type RankedPlay,
  type SectionFinding,
} from '../../modules/guide/deepDiveAnalysis';
import { GuideText } from './GuideText';
import s from './steps.module.css';

/**
 * The headline analysis, computed from the deep-dive.
 *
 * Deliberately short. This sits above the report, and an SE reading it is about
 * to read the report itself, so it answers three questions and stops: where the
 * exposure is, how much of the session was actually captured, and what to lead
 * with. The full finding tables, the rest of the plays and the weighting
 * appendix live inside the report, where they can be included or left out per
 * customer.
 */

/** The same four tones the exported charts use, so screen and document agree. */
const TONE = {
  gap: '#c62828',
  partial: '#ef8c00',
  covered: '#2e7d32',
  open: '#5a6b7f',
  unlogged: '#e3e8ee',
};

const SIGNALS = [
  { key: 'gaps', label: 'Gap', tone: TONE.gap },
  { key: 'partials', label: 'Partial', tone: TONE.partial },
  { key: 'covered', label: 'Covered', tone: TONE.covered },
  { key: 'open', label: 'No answer', tone: TONE.open },
  { key: 'unlogged', label: 'Not asked', tone: TONE.unlogged },
] as const;

export function GuideFindings({
  analysis,
  presenting,
}: {
  analysis: DeepDiveAnalysis;
  presenting?: boolean;
  /** Which half of the session this is closing. Defaults to the whole thing. */
  phase?: DeepDivePhase;
}) {
  const { scenario, sections, topPlays, asked, total } = analysis;

  if (!scenario) {
    return (
      <p className={s.emptyNote}>
        No deep-dive scenario has been chosen yet. Record where this customer stands with Cisco IQ
        on the deep-dive and this fills in from the answers.
      </p>
    );
  }

  if (asked === 0) {
    return (
      <p className={s.emptyNote}>
        Nothing recorded in the deep-dive yet. Work through the questions and this fills in from
        the answers.
      </p>
    );
  }

  return (
    <>
      <p className={s.analysisNote}>
        Scenario {scenario.n}: {scenario.title}. {asked} of {total} questions logged.
      </p>

      <div className={s.gapHero}>
        <ExposureBars sections={sections} />
        <SignalsLogged analysis={analysis} />
      </div>

      {topPlays.length > 0 ? (
        <TopPlays plays={topPlays} rest={analysis.restPlays.length} presenting={presenting} />
      ) : (
        <p className={s.emptyNote}>
          Nothing logged as a gap or a partial yet, so there is nothing to recommend. Everything
          recorded so far is already covered.
        </p>
      )}
    </>
  );
}

/** Where the exposure sits. One bar per section of the guide. */
function ExposureBars({ sections }: { sections: SectionFinding[] }) {
  const live = sections.filter((section) => section.asked > 0);
  const shown = live.length ? live : sections;

  return (
    <div className={s.gapPanel}>
      <h5 className={s.playsHead}>Exposure weight by lane</h5>
      <div className={s.gapBars}>
        {shown.map((section) => {
          const width = (n: number) => `${((n / section.total) * 100).toFixed(2)}%`;
          return (
            <div key={section.id} className={s.gapBar}>
              <span className={s.gapBarName} title={section.title}>
                {section.short}
              </span>
              <span className={s.gapBarTrack}>
                <i style={{ width: width(section.gaps), background: TONE.gap }} />
                <i style={{ width: width(section.partials), background: TONE.partial }} />
                <i style={{ width: width(section.covered), background: TONE.covered }} />
                <i style={{ width: width(section.open), background: TONE.open }} />
              </span>
              <span className={s.gapBarValue}>
                {section.asked ? `${section.exposure}%` : 'not asked'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** How much of the session was actually captured, and as what. */
function SignalsLogged({ analysis }: { analysis: DeepDiveAnalysis }) {
  const total = analysis.total || 1;
  let offset = 25; // Start the ring at twelve o'clock.
  const arcs = SIGNALS.map((signal) => {
    const value = analysis[signal.key];
    const share = (value / total) * 100;
    const arc = { ...signal, value, share, offset };
    offset -= share;
    return arc;
  });

  return (
    <div className={s.gapPanel}>
      <h5 className={s.playsHead}>Signals logged</h5>
      <div className={s.donutWrap}>
        <svg className={s.donut} viewBox="0 0 42 42" role="img" aria-labelledby="donut-title">
          <title id="donut-title">
            Share of questions logged as gap, partial, covered, unanswered and not asked
          </title>
          <circle
            cx="21"
            cy="21"
            r="15.9155"
            fill="none"
            strokeWidth="5"
            stroke="var(--surface-2)"
          />
          {arcs.map((arc) => (
            <circle
              key={arc.key}
              cx="21"
              cy="21"
              r="15.9155"
              fill="none"
              strokeWidth="5"
              stroke={arc.tone}
              strokeDasharray={`${arc.share} ${100 - arc.share}`}
              strokeDashoffset={arc.offset}
            />
          ))}
        </svg>
        <ul className={s.donutKey}>
          {arcs.map((arc) => (
            <li key={arc.key}>
              <i style={{ background: arc.tone }} />
              {arc.label}
              <b>{arc.value}</b>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** The five to lead with. The rest are a report section the SE can switch on. */
function TopPlays({
  plays,
  rest,
  presenting,
}: {
  plays: RankedPlay[];
  rest: number;
  presenting?: boolean;
}) {
  return (
    <div className={s.priorityBlock}>
      <h5 className={s.playsHead}>Recommendations</h5>
      <p className={s.analysisNote}>
        The {plays.length} the answers point at hardest.
        {rest > 0 &&
          ` ${rest} more matched; they are in the report under "Recommendations in full", which is left out unless you switch it on.`}
      </p>
      <ol className={s.priorityList}>
        {plays.map((entry, i) => (
          <li key={entry.play.id}>
            <span className={s.docRankNum}>{i + 1}</span>
            <div>
              <div className={s.priorityHead}>
                <b>{entry.play.name}</b>
                <span className={s.playScore}>{entry.score}</span>
              </div>
              {!presenting && (
                <p className={s.playDesc}>
                  <GuideText text={entry.play.detail} />
                </p>
              )}
              <p className={s.playEvidence}>
                From{' '}
                {entry.from
                  .map((from) => `${from.id} ${from.topic} (${from.status})`)
                  .join(', ')}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
